import { Injectable, signal } from '@angular/core';
import { ApiService } from './api.service';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  canInstall = signal(false);
  installed = signal(false);
  instructionsOpen = signal(false);
  isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  isAndroid = /android/i.test(navigator.userAgent);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  constructor(private api: ApiService) {
    this.refreshInstalledState();
    window.addEventListener('beforeinstallprompt', event => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.canInstall.set(true);
    });
    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.canInstall.set(false);
      this.installed.set(true);
      this.instructionsOpen.set(false);
      this.api.pingAppDevice('installed');
    });
    window.matchMedia('(display-mode: standalone)').addEventListener?.('change', () => {
      this.refreshInstalledState();
      this.api.pingAppDevice('heartbeat');
    });
  }

  async install() {
    if (this.deferredPrompt) {
      await this.deferredPrompt.prompt();
      const choice = await this.deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') this.canInstall.set(false);
      this.deferredPrompt = null;
      return;
    }
    this.instructionsOpen.set(true);
  }

  closeInstructions() { this.instructionsOpen.set(false); }

  private refreshInstalledState() {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    this.installed.set(standalone);
  }
}
